import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useSelector, useDispatch } from 'react-redux';
import { actions } from 'store/surveyTemplate';
import { AssociatedCampaignsModalDialog } from 'views/UserMessaging/customModalContent/associatedCampaigns';
import { actions as userMessageActions } from 'store/userMessaging';
import { ToolbarV2, TableComponentV2 } from 'components';
import filterHelpers from 'store/helpers/filterHelpers';
import { surveyTemplateTableTitles } from 'constants.js';
import { resetListsData } from 'store/lists/actions';
import { getEnvVar } from 'helpers';
import CopyTemplateModal from 'components/Template/CopyTemplateModal';

const {
  getPaginatedSurveyTemplates,
  deleteSurveyTemplates,
  unsetCurrentSurveyTemplate,
  setCurrentSurveyTemplate,
  setCopyCurrentSurveyTemplate,
  getSurveyTemplateTags,
  updateSurveyTemplateFilters,
  resetSurveyTemplateFilters
} = actions;

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  content: {
    marginTop: theme.spacing(2)
  }
}));

const SurveyTemplate = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    onClickToggleArchive,
    onClickAdd,
    onClickEdit,
    onClickCopy,
    isTemplateReadOnly,
    isTenantDefaultTemplate
  } = filterHelpers();
  const action = getPaginatedSurveyTemplates({ force: true });
  const templates = useSelector(store => store.surveyTemplate.paginatedList);
  const filters = useSelector(
    store => store.surveyTemplate.filters
  );
  const isLoading = useSelector(store => store.surveyTemplate.gettingPaginated);
  const productNames = useSelector(
    store => store.events.supportedEventTypes.transientProductNames
  );
  const productCategories = useSelector(
    store => store.events.supportedEventTypes.transientProductCategories
  );
  const tags = useSelector(store => store.surveyTemplate.tagList);

  const optionsToFilterTemplate = [
    {
      name: 'searchScopes',
      searchScope: 'GLOBAL DEFAULT',
      value: 'global',
      checked: false
    },
    {
      name: 'searchScopes',
      searchScope: 'TENANT DEFAULT',
      value: 'tenant',
      checked: false
    },
    {
      name: 'searchScopes',
      searchScope: 'PROPERTY LEVEL',
      value: 'property',
      checked: false
    }
  ];
  const optionsToFilterProductNames = productNames
    ? productNames
        .filter(
          productName =>
            productName !== 'ListManagement' ||
            getEnvVar('ADM_INCLUDE_LIST_MANAGEMENT_PHASE2') === 'true'
        )
        .filter(
          productName =>
            productName !== 'GuestInsight'
        )
        .map(productName => {
          return { name: 'productName', value: productName, checked: false };
        })
    : [];
  const optionsToFilterProductCategories = productCategories
    ? productCategories
        .filter(
          productCategory =>
            productCategory !== 'ADM' ||
            getEnvVar('ADM_INCLUDE_LIST_MANAGEMENT_PHASE2') === 'true'
        )
        .map(productCategory => {
          return {
            name: 'productCategory',
            value: productCategory,
            checked: false
          };
        })
    : [];
  const optionsToFilterTags = tags
    ? tags.map(tag => {
        return { name: 'tags', value: tag, checked: false };
      })
    : [];
  const [openCopyTemplateModal, setOpenCopyTemplateModal] = useState(false);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    dispatch(getPaginatedSurveyTemplates({ force: true }));
    dispatch(getSurveyTemplateTags());
    dispatch(resetListsData());
  }, []);

  const onClickArchive = uuid => {
    let action = deleteSurveyTemplates({
      templateUuids: [uuid],
      activeStatus: filters?.displayActive
    });
    let warnMessage = {
      buttons: [
        { title: 'Archive Template', action: ['DISPATCH', action] },
        { title: 'Cancel', action: 'DISMISS' }
      ],
      customContent: AssociatedCampaignsModalDialog,
      props: {
        template: { associatedCampaigns: [] },
        type: 'survey template'
      }
    };
    if (filters?.displayActive) {
      dispatch(userMessageActions.setMessage(warnMessage));
    } else {
      dispatch(action);
    }
  };

  const handleImportClick = () => {
    setOpenCopyTemplateModal(true);
  };

  const getButtonsGroup = () => [
    {
      name: `View ${filters?.displayActive ? 'Archived' : 'Active'} Templates`,
      action: () => onClickToggleArchive(action, updateSurveyTemplateFilters, { displayActive: !filters?.displayActive, pageNo: 0})
    },
    {
      name: 'Add Template',
      action: () =>
        onClickAdd(
          unsetCurrentSurveyTemplate(),
          `/surveyResultResponse-templates/details`
        )
    },
    {
      name: 'Import',
      action: () => handleImportClick()
    }
  ];

  const getActionButtons = surveyTemplate => [
    {
      name: isTemplateReadOnly(surveyTemplate, isLoading)
        ? 'View Template'
        : 'Edit Template',
      action: ['ACTION', () => onEdit(surveyTemplate)],
      ...(isTemplateReadOnly(surveyTemplate, isLoading) && {
        tooltipPlacement: 'left-start',
        tooltipTitle: isTenantDefaultTemplate(surveyTemplate)
          ? "Tenant Default template can be edited only with 'ALL' property"
          : 'Only admin can edit Global Default template'
      })
    },
    {
      name: 'Copy Template',
      action: ['ACTION', () => onCopy(surveyTemplate.uuid)]
    },
    {
      name: filters?.displayActive ? 'Archive Template' : 'Activate Template',
      action: ['ACTION', () => onClickArchive(surveyTemplate.uuid)],
      ...(isTemplateReadOnly(surveyTemplate, isLoading) && {
        isDisabled: true,
        tooltipPlacement: 'left-start',
        tooltipTitle: isTenantDefaultTemplate(surveyTemplate)
          ? "Tenant Default template can be edited only with 'ALL' property"
          : 'Only admin can edit Global Default template'
      })
    }
  ];

  const filterOptions = {
    filters: [
      {
        options: optionsToFilterTemplate.map(option => {
          if (filters?.filterTemplates?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Hierarchy',
        values: filters?.filterTemplates,
        onChange: templates => dispatch(updateSurveyTemplateFilters({ filterTemplates: templates, pageNo: 0 }))
      },
      {
        options: optionsToFilterProductNames.map(option => {
          if (filters?.filterProductNames?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Product Name',
        values: filters?.filterProductNames,
        onChange: productNames =>
          dispatch(updateSurveyTemplateFilters({ filterProductNames: productNames, pageNo: 0 }))
      },
      {
        options: optionsToFilterProductCategories.map(option => {
          if (filters?.filterProductCategories?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Product Category',
        values: filters?.filterProductCategories,
        onChange: productCategories =>
          dispatch(updateSurveyTemplateFilters({ filterProductCategories: productCategories, pageNo: 0 }))
      }
    ],
    autocompleteSearch: {
      options: optionsToFilterTags.map(option => {
        if (filters?.filterTags?.includes(option.value)) {
          option.checked = true;
        }
        return option;
      }),
      title: 'Tags',
      values: filters?.filterTags,
      onChange: tags => dispatch(updateSurveyTemplateFilters({ filterTags: tags, pageNo: 0 }))
    },
    resetFilters: () => dispatch(resetSurveyTemplateFilters())
  };

  const searchOptions = {
    searchPlaceholder: 'Search Templates',
    searchAction: (payload) => dispatch(updateSurveyTemplateFilters(payload)),
    searchKey: 'searchInput',
    searchValue: filters?.searchInput || ''
  };

  const onEdit = row =>
    onClickEdit(
      [unsetCurrentSurveyTemplate(), setCurrentSurveyTemplate(row)],
      `/surveyResultResponse-templates/details/${row.uuid}`
    );
  const onCopy = uuid =>
    onClickCopy(
      setCopyCurrentSurveyTemplate,
      `/surveyResultResponse-templates/details`,
      uuid,
      templates
    );

  return (
    <div className={classes.root}>
      <ToolbarV2
        searchOptions={searchOptions}
        filterOptions={filterOptions}
        buttonsGroup={getButtonsGroup()}
        action={getPaginatedSurveyTemplates({ force: true })}
      />
      <div className={classes.content}>
        <TableComponentV2
          isLoading={isLoading}
          tableTitles={surveyTemplateTableTitles}
          onDoubleClickRow={row => onEdit(row)}
          actionButtonOptions={row => getActionButtons(row)}
          filteredRows={templates}
          action={getPaginatedSurveyTemplates({ force: true })}
          filterValues={filters}
          filterAction={(payload) => dispatch(updateSurveyTemplateFilters(payload))}
        />
      </div>
      {openCopyTemplateModal && (
        <CopyTemplateModal
          isOpen={openCopyTemplateModal}
          closeModal={() => setOpenCopyTemplateModal(false)}
        />
      )}
    </div>
  );
};

export default SurveyTemplate;
